<template>
  <Card class="rounded-2xl p-0 shadow-md">
    <CardHeader class="p-6">
      <CardTitle class="text-[20px] font-semibold">Notifications</CardTitle>
    </CardHeader>
    <CardContent class="p-0">
      <div v-if="!notifications.length" class="p-6">
        <p class="text-center text-sm text-grey-light-400">No conversations / notes</p>
      </div>

      <!-- Mobile: stacked layout -->
      <div v-else-if="isMobileOrTablet">
        <div v-for="(item, index) in notifications" :key="index" class="border-t">
          <div class="bg-grey-light-50 px-6 py-2 text-[14px] font-medium text-grey-light-700">Date</div>
          <div class="px-6 py-3 text-[14px] text-grey-light-900">{{ item.date }}</div>
          <div class="bg-grey-light-50 px-6 py-2 text-[14px] font-medium text-grey-light-700">Name</div>
          <div class="px-6 py-3 text-[14px] text-grey-light-600">{{ item.name }}</div>
          <div class="bg-grey-light-50 px-6 py-2 text-[14px] font-medium text-grey-light-700">Reason</div>
          <div class="px-6 py-3 text-[14px] text-grey-light-600">{{ item.reason }}</div>
          <div class="bg-grey-light-50 px-6 py-2 text-[14px] font-medium text-grey-light-700">Details</div>
          <div class="px-6 py-3 text-[14px] text-grey-light-600">
            <div class="flex items-center justify-between gap-2">
              <span>{{ cleanDetails(item.details) }}</span>
              <button
                v-if="canReply(item)"
                class="text-grey-light-400 hover:text-grey-light-600 shrink-0"
                @click="$emit('reply')"
              >
                <ReplyIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop: table layout -->
      <Table v-else>
        <TableHeader>
          <TableRow class="bg-grey-light-50 border-t">
            <TableHead class="text-[14px] font-medium text-grey-light-700 pl-6">Date</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700">Name</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700">Reason</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700 pr-6">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="(item, index) in notifications" :key="index" class="border-b border-grey-light-100">
            <TableCell class="text-[14px] text-grey-light-900 pl-6 font-medium">{{ item.date }}</TableCell>
            <TableCell class="text-[14px] text-grey-light-600">{{ item.name }}</TableCell>
            <TableCell class="text-[14px] text-grey-light-600">{{ item.reason }}</TableCell>
            <TableCell class="text-[14px] text-grey-light-600 pr-6">
              <div class="flex items-center justify-between gap-2">
                <span>{{ cleanDetails(item.details) }}</span>
                <button
                  v-if="canReply(item)"
                  class="text-grey-light-400 hover:text-grey-light-600 shrink-0"
                  @click="$emit('reply')"
                >
                  <ReplyIcon class="h-4 w-4" />
                </button>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import ReplyIcon from '@/components/icons/ReplyIcon.vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUserStore } from '@/stores/users';
import dayjs from '@/utils/dayjs.utils';
import { cleanDetails } from '@/utils/text.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { computed } from 'vue';
import type { ModelNote, ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  order?: ModelOrderResponse;
}>();

defineEmits<{ reply: [] }>();

const { user } = useUserStore();
const { isMobile, isTablet } = useBreakpoint();
const isMobileOrTablet = computed(() => isMobile.value || isTablet.value);

const notifications = computed(() => {
  if (!props.order?.notifications?.length) return [];
  return [...props.order.notifications].sort((a, b) => (dayjs(b.date).isAfter(dayjs(a.date)) ? 1 : -1));
});

const canReply = (item: ModelNote) => {
  const last = notifications.value.at(0);
  return (
    item.userId !== user?._id &&
    !item.read &&
    item.change !== 'REJECTED' &&
    item.change !== 'ACCEPTED' &&
    item.type === 'CANCELLATION' &&
    last?.change !== 'REFUNDED_BY_MERCHANT' &&
    props.order?.state === 'PLACED'
  );
};
</script>
